import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';

import { FormlyFieldConfig } from '@ngx-formly/core';

import { CoreService, DspxBaseTableRepeaterComponent, FIELD_TYPE, RepeaterRegistryService, RepeaterService, SpinnerService } from 'dspx-core';

import { APP_DATA_KEYS, COLLAPSIBLE_SECTION_TIMING } from '../../constants/app-data.constants';

import { LoggedInUserRoleFlags } from '../../interfaces/logged-in-user-role-flags.interface';

import { AppDataService } from '../../services/app-data.service';

@Component({
  selector: 'dspx-card-section-repeating-collapsible',
  templateUrl: './card-section-repeating-collapsible.component.html',
  styleUrl: './card-section-repeating-collapsible.component.scss',
})
export class CardSectionRepeatingCollapsibleFieldType extends DspxBaseTableRepeaterComponent implements OnInit, OnDestroy {

  sectionBodyFields: FormlyFieldConfig[] = [];
  existsField: FormlyFieldConfig;
  notAssessedField: FormlyFieldConfig;
  questionCountInSection: number = 0;
  existingData: any[] = []; 
  previousItemsCount: number = 0;
  isSectionMandatory: boolean = false;
  lastInspectionDate: any = null;
  canUseExistingData: boolean = false;

  constructor(
    protected coreService: CoreService,
    protected spinnerService: SpinnerService,
    protected repeaterRegistryService: RepeaterRegistryService,
    protected repeaterService: RepeaterService,
    protected changeDetector: ChangeDetectorRef,
    private appDataService: AppDataService) {
    super(
      coreService,
      spinnerService,
      repeaterRegistryService,
      repeaterService,
      repeaterService.getSectionRepeaterExtraSettings(),
      changeDetector);
  }

  ngOnInit(): void {
    this.initialise();
    this.getFields();
    this.field.props.totalQuestionCount = this.sectionBodyFields.length;
    this.field.props.isCollapsed = true;
    this.field.props.exists = true;
    this.field.props.notAssessed = false;
    this.field.props.answeredQuestionCount = this.getAnswerCount(this.field);
    this.canUseExistingData = this.canUseExistingInspectionData();
    this.repeaterProperties.isEditable = this.canEditInspection();
    this.field.formControl?.valueChanges.subscribe(value => {
      this.trackFieldChanges(this.field);
    });

    this.field.fieldGroup
      .flatMap(field => field.fieldGroup)
      .forEach(
        (question) => {
          if (this.isExistsQuestion(question.key.toString())) {
            this.field.props.exists = question.formControl.value;
          }

          if (this.isNotAssessedQuestion(question.key.toString())) {
            this.field.props.notAssessed = question.formControl.value;
          }
        });

    this.questionCountInSection = this.field.props.totalQuestionCount;

    //load most recent previous data
    const previousInspectionData = this.field.props?.previousInspectionData;
    if (previousInspectionData && this.existingData.length === 0) {
      this.loadPreviousDataForSection(previousInspectionData);
    }
    this.lastInspectionDate = this.field.props?.lastInspectionDate;
  }

  private canEditInspection(): boolean {
    const loggedInUserRoleIsPcrInspector = () =>
      this.appDataService.getAppData<LoggedInUserRoleFlags>(APP_DATA_KEYS.loggedInUserRoleFlags)?.isPcrInspector ?? false;

    const loggedInUserRoleIsPcrVendorManager = () =>
      this.appDataService.getAppData<LoggedInUserRoleFlags>(APP_DATA_KEYS.loggedInUserRoleFlags)?.isPcrVendorManager ?? false;

    return loggedInUserRoleIsPcrInspector() || loggedInUserRoleIsPcrVendorManager();
  }

  private canUseExistingInspectionData(): boolean {
    return this.appDataService.getAppData<LoggedInUserRoleFlags>(APP_DATA_KEYS.loggedInUserRoleFlags)?.isPcrInspector ?? false;
  }

  trackFieldChanges(field: FormlyFieldConfig): void {
    if (field.model.length > 0) {
      this.field.props.totalQuestionCount = this.questionCountInSection * field.model.length;      
      this.field.props.answeredQuestionCount = this.getAnswerCount(field);
    }
  }

  getAnswerCount(field: FormlyFieldConfig): number {
    if (field.model) {
      return field.model.reduce((acc, obj) => {
        Object.keys(obj).forEach(key => {
          if (!this.isHeaderQuestion(key) && obj[key] !== null && obj[key] !== '') {
            acc++;
          }
        });
        return acc;
      }, 0);
    }
  }

  clearSectionModel(field: FormlyFieldConfig): void {
    if (field.model.length > 0) {
      field.fieldGroup.forEach(repeatingItem => {
        repeatingItem.fieldGroup.forEach(question => {
          if (!this.isHeaderQuestion(question.key.toString()) && !this.isEditingQuestion(question.key.toString())) {
            question.formControl.setValue(undefined);
            delete question.model[question.key.toString()];
          }
        })
      })
    }
  }

  getFields(): void {
    this.existsField = this.field.fieldGroup[0].fieldGroup.find(f => this.isExistsQuestion(f.key.toString()));
    this.notAssessedField = this.field.fieldGroup[0].fieldGroup.find(f => this.isNotAssessedQuestion(f.key.toString()));
    this.sectionBodyFields = this.field.fieldGroup[0].fieldGroup.filter(f => !this.isHeaderQuestion(f.key.toString()));

    if (this.notAssessedField?.props?.all?.length > 0) {
      this.notAssessedField.props.all.pop();
    }

    this.existsField?.formControl?.valueChanges.subscribe(value => {
      this.updateSectionModel(this.existsField);
    });

    if (this.existsField?.props?.access === 'R') {
      this.isSectionMandatory = true;
    }

    this.notAssessedField?.formControl?.valueChanges.subscribe(value => {
      this.updateSectionModel(this.notAssessedField);
    });
  }

  updateSectionModel(field: FormlyFieldConfig): void {
    if (this.isExistsQuestion(field.key.toString())) {
      this.field.props.exists = field.formControl.value;

      if (!this.field.props.exists) {
        this.field.props.isCollapsed = true;
        this.notAssessedField.props.disabled = true;
        if (this.notAssessedField.formControl.value) {
          this.notAssessedField.formControl.setValue(false);
        }
        this.clearSectionModel(this.field);
      }
      else {
        this.notAssessedField.props.disabled = false;
      }
    }
    if (this.isNotAssessedQuestion(field.key.toString())) {
      this.field.props.notAssessed = field.formControl.value;
      if (this.field.props.notAssessed) {
        this.field.props.isCollapsed = true;
        this.clearSectionModel(this.field);
      }
    }
  }

  setCollapsedStatus(field: FormlyFieldConfig): void {
    const currentSectionKey = field.key;
    field.props.isCollapsed = !field.props.isCollapsed;
    field.parent?.parent?.parent?.fieldGroup?.forEach(tab => {
      tab.fieldGroup.forEach(page => {
        page.fieldGroup.forEach(section => {
          if (section.key !== currentSectionKey && !section.props.isCollapsed)
            section.props.isCollapsed = true;
        })
      })
    })
  }

  private loadPreviousDataForSection(previousInspectionData: any): void {
    const sectionKey = this.field.key?.toString();

    const sectionData = this.findSectionDataInPreviousInspection(previousInspectionData, sectionKey);

    if (!sectionData || !Array.isArray(sectionData)) {
      return;
    }

    this.existingData = [];

    sectionData.forEach((item, itemIndex) => {
      Object.keys(item).forEach(fieldKey => {

        if (this.isHeaderQuestion(fieldKey)) {
          return;
        }

        const previousValue = item[fieldKey];

        if (previousValue !== undefined && previousValue !== null && previousValue !== '') {
          const fieldConfig = this.sectionBodyFields.find(f => f.key.toString() === fieldKey);

          if (fieldConfig) {
            const tempField = { ...fieldConfig, model: { [fieldKey]: previousValue } };
            const displayValue = this.coreService.getFieldValue(tempField) || previousValue?.toString() || '';

            const fieldLabel = fieldConfig.props?.label || fieldKey;

            this.existingData.push({
              label: fieldLabel,
              value: displayValue,
              key: fieldKey,
              code: previousValue,
              itemIndex: itemIndex,
              fieldType: fieldConfig.type
            });
          }
        }
      });
    });

    this.previousItemsCount = sectionData.length;
  }

  private findSectionDataInPreviousInspection(previousInspectionData: any, sectionKey: string): any {
    const form = previousInspectionData?.form;
    if (!form?.tabs) {
      return null;
    }

    for (const tabKey in form.tabs) {
      const tab = form.tabs[tabKey];
      for (const pageKey in tab) {
        const page = tab[pageKey];
        if (page && page[sectionKey]) {
          return page[sectionKey];
        }
      }
    }

    return null;
  }

  onUseExistingData(): void {
    if (!this.existingData || this.existingData.length === 0) {
      return;
    }

    const currentItemCount = this.baseTableRepeater.viewModel.model.length;
    const itemsMap = new Map<number, any[]>();

    this.existingData.forEach(dataItem => {
      if (!itemsMap.has(dataItem.itemIndex)) {
        itemsMap.set(dataItem.itemIndex, []);
      }
      itemsMap.get(dataItem.itemIndex).push(dataItem);
    });

    const neededItemCount = itemsMap.size;
    const itemsToAdd = Math.max(0, neededItemCount - currentItemCount);

    // Add required number of items
    for (let i = 0; i < itemsToAdd; i++) {
      this.baseTableRepeater.addItem();
    }

    // Collapse all sections before updating
    this.field.props.isCollapsed = true;
    this.changeDetector.detectChanges();

    setTimeout(() => {
      // Populate data for each item
      itemsMap.forEach((fieldsData, itemIndex) => {
        const itemConfig = this.baseTableRepeater.getItemConfig(itemIndex);

        if (!itemConfig || !itemConfig.fieldGroup) {
          return;
        }

        // Populate fields for this specific item
        fieldsData.forEach(existingDataItem => {
          const fieldConfig = itemConfig.fieldGroup.find(f => f.key?.toString() === existingDataItem.key);

          if (fieldConfig && fieldConfig.formControl) {
            
            const fieldKey = fieldConfig.key.toString();
            const options = fieldConfig.props?.options;

            let valueToApply = existingDataItem.code;

            if (Array.isArray(options)) {
              const valueExists = options.some(opt => opt.code === existingDataItem.code);
              if (!valueExists) {
                // If the GUID doesn't exist, set to null to trigger validation
                valueToApply = null;
              }
            }

            fieldConfig.model[fieldKey] = valueToApply;

            // Set the form control value
            fieldConfig.formControl.setValue(valueToApply);
            fieldConfig.formControl.markAsTouched();
            fieldConfig.formControl.markAsDirty();
            fieldConfig.formControl.updateValueAndValidity();
          }
        });
      });

      // Expand sections to trigger re-initialization
      this.field.props.isCollapsed = false;
      this.changeDetector.detectChanges();

      // Final updates after components have re-initialized
      setTimeout(() => {
        this.field.formControl?.updateValueAndValidity();
        this.field.props.answeredQuestionCount = this.getAnswerCount(this.field);
        this.changeDetector.detectChanges();
      }, COLLAPSIBLE_SECTION_TIMING.expandWaitMs);
    }, COLLAPSIBLE_SECTION_TIMING.collapseWaitMs);
  }

  getGroupedData() {
    if (!this.existingData || this.existingData.length === 0) {
      return [];
    }

    const sorted = [...this.existingData].sort((a, b) => a.itemIndex - b.itemIndex);

    const grouped = sorted.reduce((acc, curr) => {
      let group = acc.find(g => g.itemIndex === curr.itemIndex);
      if (!group) {
        group = { itemIndex: curr.itemIndex, items: [] };
        acc.push(group);
      }
      group.items.push(curr);
      return acc;
    }, []);

    grouped.sort((a, b) => a.itemIndex - b.itemIndex);
    grouped.forEach(g => g.displayItemNo = g.itemIndex + 1);

    return grouped;
  }

  getDisplayValue(value: any, type: any): string {
    if (!value && value !== 0) {
      return '';
    }

    const stringValue = String(value);

    if (type === FIELD_TYPE.file) {
      return stringValue.split(':')[0];
    }

    return stringValue;
  }

  getQuestionLabel(label: string): string {
    if (label.includes('Remaining Life')) {
      return 'Calculated ' + label;
    }
    return label;
  }

  isExistsQuestion = (questionCode: string): boolean => questionCode.endsWith('-exists');
  isNotAssessedQuestion = (questionCode: string): boolean => questionCode.endsWith('-notassessed');
  isEditingQuestion = (questionCode: string): boolean => questionCode === 'isEditing';
  isHeaderQuestion = (questionCode: string): boolean => this.isExistsQuestion(questionCode) || this.isNotAssessedQuestion(questionCode) || this.isEditingQuestion(questionCode);

  ngOnDestroy(): void {
    this.destroy();
  }
}
